import React from 'react';
import { REPORT } from '../print/PrintAtoms';
import { PORTFOLIO_TOKENS, toneStyle, gradeTone, splitGrade } from './tokens';
import type { PortfolioRow as PortfolioRowType, PortfolioDimension, Tone } from '../../lib/portfolioApi';
import { pipelinePotential, type PipelinePotential } from '../../lib/pipeline-reference';

interface Props {
  row: PortfolioRowType;
  rowNumber: number;
  isActive?: boolean;
  isHovered?: boolean;
  isExpanded?: boolean;
  onSelect?: (parcelId: string) => void;
  onHover?: (parcelId: string | null) => void;
  onToggleExpand?: (parcelId: string) => void;
}

const fmtNum = (n: number | null) => (n == null ? '—' : n.toLocaleString());

// Pick the subtitle for the Acres cell. Carve-out tract count when this row
// is a rep parcel for a multi-tract operator carve-out, otherwise nothing.
function acresSubtitle(row: PortfolioRowType): string | null {
  // Clean pad is the headline when present. The Acres cell is narrow and
  // truncates, so lead with the pad figure alone (the tract count still shows
  // in the side panel + the carve-out chip).
  if (row.clean_pad_acres != null) {
    return `${fmtNum(row.clean_pad_acres)} clean`;
  }
  if (row.carve_out && row.carve_out.parcel_count > 1) {
    return `${row.carve_out.parcel_count}-tract block`;
  }
  return null;
}

// Pipeline cell. Only the approved reference scenario gets a number, rounded;
// other sites say what is on record. Never an "available" claim, never summed.
// The note line is ~68 px at desktop width: keep it to 14 characters. A
// recorded-only site's pipe already shows in the row's "on <pipeline>" line.
function pipelineCell(potential: PipelinePotential): { value: string; unit?: string; note: string } {
  switch (potential.kind) {
    case 'modeled': {
      const miles = potential.distanceMiles;
      const distance = miles === 0 ? 'on site'
        : miles < 0.1 ? '<0.1 mi'
        : `${miles.toLocaleString('en-US', { maximumFractionDigits: 1 })} mi`;
      return {
        value: `≈${potential.displayMw.toLocaleString('en-US')}`,
        unit: 'MW',
        note: `${potential.diameterInches}″ ${distance}`,
      };
    }
    case 'recorded':
      return { value: '—', note: 'Not modeled' };
    case 'no_line':
      return { value: '—', note: 'None eligible' };
    default:
      return { value: '—', note: 'Not assessed' };
  }
}

// Reusable mini KeyNumber cell. `noteTone` controls the small subtitle line's
// color independently of `tone` (which colors the main value). Defaults to
// caution to preserve existing risk-style note rendering; pass 'pass' or
// 'neutral' for positive subtitles like "N-tract assembly".
const KeyCell: React.FC<{ value: string; unit?: string; label: string; note?: string | null; tone?: Tone; noteTone?: Tone }> = ({ value, unit, label, note, tone, noteTone = 'caution' }) => {
  const style = tone && tone !== 'neutral' ? toneStyle(tone) : null;
  const noteStyle = noteTone === 'caution' ? toneStyle('caution')
    : noteTone === 'pass' ? toneStyle('pass')
    : noteTone === 'fail' ? toneStyle('fail')
    : null;
  return (
    <div style={{
      padding: '8px 11px',
      borderRight: `1px solid ${REPORT.rule}`,
      minWidth: 0,
    }}>
      <div style={{
        fontFamily: PORTFOLIO_TOKENS.serif,
        fontSize: 16,
        lineHeight: 1.05,
        color: style ? style.fg : REPORT.heading,
        fontWeight: 400,
        letterSpacing: '-0.005em',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
        {value}
        {unit && (
          <span style={{
            fontFamily: PORTFOLIO_TOKENS.mono,
            fontSize: 9,
            color: REPORT.tertiary,
            letterSpacing: '0.04em',
            marginLeft: 2,
            fontWeight: 400,
          }}>{unit}</span>
        )}
      </div>
      <div style={{
        fontFamily: PORTFOLIO_TOKENS.mono,
        fontSize: 8,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: REPORT.secondary,
        marginTop: 2,
      }}>{label}</div>
      {note && (
        <div style={{
          fontFamily: PORTFOLIO_TOKENS.mono,
          fontSize: 7.5,
          color: noteStyle ? noteStyle.fg : REPORT.tertiary,
          marginTop: 1,
          letterSpacing: '0.02em',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>{note}</div>
      )}
    </div>
  );
};

// Compact 4-up DimensionBar for in-row use.
const DimCell: React.FC<{ label: string; dim: PortfolioDimension }> = ({ label, dim }) => {
  const style = toneStyle(dim.tone);
  const { letter, sign } = splitGrade(dim.grade);
  return (
    <div style={{
      padding: '6px 8px',
      borderRadius: 4,
      border: `1px solid ${style.border}`,
      background: style.bg,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      minWidth: 0,
    }}>
      <div style={{
        fontFamily: PORTFOLIO_TOKENS.mono,
        fontSize: 7.5,
        fontWeight: 600,
        letterSpacing: '0.10em',
        textTransform: 'uppercase',
        color: style.fg,
      }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, justifyContent: 'space-between' }}>
        <div style={{
          fontFamily: PORTFOLIO_TOKENS.serif,
          fontSize: 16,
          lineHeight: 1,
          fontWeight: 400,
          color: style.fg,
          letterSpacing: '-0.01em',
        }}>
          {letter}
          {sign && (
            <span style={{ fontSize: 10, opacity: 0.7, marginLeft: -1, verticalAlign: 3 }}>{sign}</span>
          )}
        </div>
        <div style={{
          fontFamily: PORTFOLIO_TOKENS.mono,
          fontSize: 8,
          color: REPORT.tertiary,
          letterSpacing: '0.02em',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textAlign: 'right',
          flex: 1,
        }}>{dim.driver}</div>
      </div>
    </div>
  );
};

const ResolveTag: React.FC<{ count: number; isPark: boolean }> = ({ count, isPark }) => {
  const tone: Tone = isPark || count >= 3 ? 'fail' : count > 0 ? 'caution' : 'pass';
  const style = toneStyle(tone);
  return (
    <span style={{
      fontFamily: PORTFOLIO_TOKENS.mono,
      fontSize: 9,
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      padding: '3px 7px',
      borderRadius: 3,
      border: `1px solid ${style.border}`,
      background: style.bgWash,
      color: style.fg,
      textAlign: 'center',
      lineHeight: 1.3,
      display: 'inline-flex',
      flexDirection: 'column',
      minWidth: 44,
    }}>
      <span style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.1 }}>{count}</span>
      <span style={{ fontSize: 7.5, letterSpacing: '0.08em' }}>
        {isPark ? 'drop' : count === 1 ? 'item' : 'items'}
      </span>
    </span>
  );
};

const MiniGradeCard: React.FC<{ grade: string | null; isPark: boolean }> = ({ grade, isPark }) => {
  const tone = isPark ? 'neutral' : gradeTone(grade);
  const style = toneStyle(tone);
  const { letter, sign } = splitGrade(grade);
  return (
    <div style={{
      width: 60,
      textAlign: 'center',
      padding: '6px 4px 5px',
      borderRadius: 5,
      border: `1px solid ${tone === 'neutral' ? REPORT.rule : style.border}`,
      background: tone === 'neutral' ? REPORT.surface : style.bg,
    }}>
      <div style={{
        fontFamily: PORTFOLIO_TOKENS.serif,
        fontSize: 30,
        fontWeight: 400,
        lineHeight: 1,
        letterSpacing: '-0.02em',
        color: tone === 'neutral' ? REPORT.tertiary : style.fg,
      }}>
        {letter}
        {sign && (
          <span style={{ fontSize: 18, marginLeft: -2, opacity: 0.7, verticalAlign: 6 }}>{sign}</span>
        )}
      </div>
      <div style={{
        fontFamily: PORTFOLIO_TOKENS.mono,
        fontSize: 7.5,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        marginTop: 3,
        color: tone === 'neutral' ? REPORT.tertiary : style.fg,
      }}>
        {isPark ? 'Park' : 'Grade'}
      </div>
    </div>
  );
};

export const PortfolioRow: React.FC<Props> = ({
  row, rowNumber,
  isActive = false,
  isHovered = false,
  isExpanded = false,
  onSelect,
  onHover,
  onToggleExpand,
}) => {
  const isPark = row.priority_tag === 'park';
  const stateLabel = row.state || '';

  // Site headline — prefer "<County> County <subtitle>" when we have a label that
  // isn't just the county name, otherwise plain "<County> County".
  const headline = `${row.county} County`;
  const sub = row.site_label && row.site_label !== `${row.county} County` ? row.site_label : null;

  // Pipeline meta line: "on <Pipeline> · <op> · <diameter>″"
  const pipeMeta = row.pipeline
    ? `on ${row.pipeline.name}${row.pipeline.diameter_in ? ` · ${row.pipeline.diameter_in}″` : ''}`
    : null;
  const pipe = pipelineCell(pipelinePotential(row));

  // Hovered = mirrored from map dot hover (or eventually row hover). Visual
  // emphasis is mild — just enough to let the eye track which list row the
  // dot belongs to. Active is the stronger "clicked" state.
  const bg = isActive ? PORTFOLIO_TOKENS.sand : isHovered ? PORTFOLIO_TOKENS.amberWash : REPORT.white;
  const borderColor = isActive ? REPORT.accent : isHovered ? REPORT.accent : REPORT.border;

  return (
    <article
      data-parcel-row-id={row.parcel_id}
      onClick={() => onSelect?.(row.parcel_id)}
      onMouseEnter={e => {
        onHover?.(row.parcel_id);
        if (!isActive) (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 12px -6px rgba(46,56,64,0.18)';
      }}
      onMouseLeave={e => {
        onHover?.(null);
        if (!isActive) (e.currentTarget as HTMLElement).style.boxShadow = 'none';
      }}
      style={{
        background: bg,
        border: `1px solid ${borderColor}`,
        borderRadius: 6,
        padding: '14px 16px',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: '48px 230px 1fr 392px 150px',
        gap: 14,
        alignItems: 'center',
        opacity: isPark ? 0.66 : 1,
        boxShadow: isActive ? '0 6px 18px -10px rgba(196,112,90,0.45)' : 'none',
        // Small scroll-into-view margin so an active row doesn't jam against
        // the top edge of the rows pane (gives a hair of breathing room from
        // the column header above).
        scrollMarginTop: 8,
      }}
    >
      {/* Rank stamp */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
        <span style={{
          fontFamily: PORTFOLIO_TOKENS.mono,
          fontSize: 14,
          fontWeight: 700,
          color: REPORT.heading,
          background: REPORT.sand,
          padding: '4px 7px',
          borderRadius: 3,
          lineHeight: 1,
          minWidth: 32,
          textAlign: 'center',
          letterSpacing: '0.04em',
        }}>{String(rowNumber).padStart(2, '0')}</span>
        <span style={{
          fontFamily: PORTFOLIO_TOKENS.mono,
          fontSize: 8.5,
          fontWeight: 600,
          letterSpacing: '0.10em',
          color: REPORT.secondary,
          textTransform: 'uppercase',
        }}>{stateLabel}</span>
      </div>

      {/* Title block */}
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{
          fontFamily: PORTFOLIO_TOKENS.serif,
          fontSize: 17,
          lineHeight: 1.15,
          color: REPORT.heading,
          fontWeight: 400,
          letterSpacing: '-0.005em',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {headline}
          {sub && (
            <span style={{ color: REPORT.secondary, fontSize: 14, marginLeft: 6 }}>
              {sub}
            </span>
          )}
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontFamily: PORTFOLIO_TOKENS.mono,
          fontSize: 8.5,
          letterSpacing: '0.06em',
          color: REPORT.tertiary,
          textTransform: 'uppercase',
        }}>
          {row.priority_label && (
            <span style={{
              color: isPark ? REPORT.fail : REPORT.accent,
              fontWeight: 700,
              letterSpacing: '0.10em',
            }}>{row.priority_label}</span>
          )}
        </div>
        {pipeMeta && (
          <div style={{
            fontFamily: PORTFOLIO_TOKENS.mono,
            fontSize: 9,
            letterSpacing: '0.04em',
            color: REPORT.secondary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {pipeMeta}
          </div>
        )}
      </div>

      {/* KeyNumbers connected strip — 4-up: Acres (with assembly subtitle when
          carve_out exists) · Pipeline MW · Air Permit (TCEQ band) · Site Risk
          (AML/flood/slope priority). Air Permit gets its own cell because
          TCEQ approval is the BTM regulatory gate in Texas. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        background: REPORT.surface,
        borderRadius: 5,
        overflow: 'hidden',
      }}>
        <KeyCell
          value={fmtNum(row.key_numbers.acres)}
          unit="ac"
          label="Acreage"
          note={acresSubtitle(row)}
          noteTone="pass"
        />
        <KeyCell value={pipe.value} unit={pipe.unit} label="Reference" note={pipe.note} noteTone="neutral" />
        <KeyCell value={row.key_numbers.air_permit.value} label={row.key_numbers.air_permit.label} note={row.key_numbers.air_permit.note} tone={row.key_numbers.air_permit.tone} />
        <KeyCell value={row.key_numbers.site_risk.value} label={row.key_numbers.site_risk.label} note={row.key_numbers.site_risk.note} tone={row.key_numbers.site_risk.tone} />
      </div>

      {/* DimensionBar 5-up — Integra's priority order: Power · Land · Owner · Fiber · Water.
          Clicking this strip toggles the row's expanded detail panel. Body
          click only sets active; grade click is the explicit expand affordance. */}
      <div
        onClick={(e) => {
          e.stopPropagation();
          // Selecting + toggling expansion in one action — body click without
          // this would otherwise just set active without revealing detail.
          onSelect?.(row.parcel_id);
          onToggleExpand?.(row.parcel_id);
        }}
        role="button"
        aria-expanded={isExpanded}
        aria-label={isExpanded ? 'Collapse grade detail' : 'Expand grade detail'}
        title={isExpanded ? 'Click to collapse grade detail' : 'Click to view grade detail'}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: 5,
          cursor: 'pointer',
          padding: 2,
          margin: -2,
          borderRadius: 5,
          transition: 'background 0.15s ease',
          background: isExpanded ? PORTFOLIO_TOKENS.sand : 'transparent',
        }}
        onMouseEnter={e => { if (!isExpanded) (e.currentTarget as HTMLElement).style.background = PORTFOLIO_TOKENS.amberWash; }}
        onMouseLeave={e => { if (!isExpanded) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        <DimCell label="Power" dim={row.dimensions.power} />
        <DimCell label="Land" dim={row.dimensions.land} />
        <DimCell label="Owner" dim={row.dimensions.ownership} />
        <DimCell label="Fiber" dim={row.dimensions.fiber} />
        <DimCell label="Water" dim={row.dimensions.water} />
      </div>

      {/* Right rail — MiniGradeCard click also toggles expansion, mirroring
          the DimensionBar affordance. Click anywhere "grade" → see the
          breakdown panel with the score histogram and per-dimension cards. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
        <ResolveTag count={row.resolve_count} isPark={isPark} />
        <div
          onClick={(e) => {
            e.stopPropagation();
            onSelect?.(row.parcel_id);
            onToggleExpand?.(row.parcel_id);
          }}
          role="button"
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Collapse grade detail' : 'View grade breakdown'}
          title={isExpanded ? 'Click to collapse breakdown' : 'Click to view grade breakdown'}
          style={{
            cursor: 'pointer',
            borderRadius: 5,
            transition: 'background 0.15s ease',
            background: isExpanded ? PORTFOLIO_TOKENS.sand : 'transparent',
          }}
          onMouseEnter={e => { if (!isExpanded) (e.currentTarget as HTMLElement).style.background = PORTFOLIO_TOKENS.amberWash; }}
          onMouseLeave={e => { if (!isExpanded) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
        >
          <MiniGradeCard grade={row.grade} isPark={isPark} />
        </div>
      </div>

      {/* Optional row note */}
      {row.display_note && (
        <div style={{
          gridColumn: '2 / -1',
          fontFamily: PORTFOLIO_TOKENS.sans,
          fontSize: 11.5,
          lineHeight: 1.4,
          color: REPORT.body,
          padding: '7px 12px',
          background: PORTFOLIO_TOKENS.amberWash,
          borderRadius: 4,
          marginTop: 4,
        }}>
          <strong style={{ color: REPORT.heading, fontWeight: 600 }}>Note:</strong>{' '}
          {row.display_note}
        </div>
      )}

      {/* Detail panel lives outside the row — see PortfolioSidePanel. The
          expanded state still tints the right-rail grade chip so the user
          knows which row's detail the panel is showing. */}
    </article>
  );
};

